import * as React from "react"
import { ConnectionStatusType } from "@/types"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import Cookies from "js-cookie"
import { useForm } from "react-hook-form"
import * as z from "zod"

import { editConnectionStatus } from "@/lib/api/connection-status"
import { useToast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { DropdownMenuSeparator } from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Header } from "@/components/header"

import { Icons } from "../icons"
import { Card } from "../ui/card"
import { Input } from "../ui/input"
import { Textarea } from "../ui/textarea"

const formSchema = z.object({
  sequence: z.coerce.number().min(1, "Sequence is required"),
  status_name: z.string().min(1, "Status name  is required"),
  description: z.string().min(1, "Description is required"),
  icon: z.string().min(1,"Icon is required"),
})

export default function EditConnectionStatusForm({
  connectionStatusData,
  setShowEditConnectionStatusModal,
}: {
  connectionStatusData: ConnectionStatusType
  setShowEditConnectionStatusModal: React.Dispatch<
    React.SetStateAction<boolean>
  >
}) {
  const { toast } = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      sequence: connectionStatusData?.sequence ?? undefined,
      icon: connectionStatusData?.icon ?? "",
      status_name: connectionStatusData?.status_name ?? "",
      description: connectionStatusData?.description ?? "",
    },
  })

  const mutation = useMutation({
    mutationFn: async (data: ConnectionStatusType) => {
      if (connectionStatusData?.id) {
        await editConnectionStatus(connectionStatusData?.id, data)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["connectionStatusDetails"],
      })

      toast({
        description: "Connection Status updated successfully.",
      })

      setShowEditConnectionStatusModal(false)
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again.",
        variant: "destructive",
      })
    },
  })

  function handleSubmit(values: z.infer<typeof formSchema>) {
    //console.log("Submitting values:", values)
    mutation.mutate(values as ConnectionStatusType)
  }

  return (
    <div className="px-2">
      <Header
        subroutes={[
          {
            route: "/connection-status",
            label: "Connection Status",
          },
        ]}
      >
        Edit Connection Status
      </Header>
      <DropdownMenuSeparator className="mt-4" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="grid grid-cols-1 gap-4 lg:container md:max-w-[1000px]"
        >
          <Card className="my-4 gap-4 p-6">
            <FormField
              control={form.control}
              name="status_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Status Name<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Description<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sequence"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Sequence<span className="text-red-600">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="number" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="icon"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Icon</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Card>
          <div className="flex flex-row justify-end gap-2 md:mr-4">
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowEditConnectionStatusModal(false)
              }}
              className="w-28"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending}
              className="w-28"
            >
              {mutation.isPending && (
                <Icons.loader className="mr-2 animate-spin" />
              )}
              Save Changes
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
